/**
 * 履歴画面の URL から、どの階層を表示するかを決める純粋関数 (マスタ変更履歴 SBI 7)。
 *
 * - `/master-history`                      → ① 変更一覧
 * - `/master-history?entity_key=`          → ② 対象データの履歴一覧
 * - `/master-history?entity_key=&rev=`     → ③ 履歴詳細 (変更通知メールのリンクの着地点)
 *
 * 判定は URL だけで行う (API は呼ばない)。版が実在するかは ③ を開いたときの API (404) で分かる。
 * 画面から切り離してあるのは、①② と ③ を別の PR に分けても判定ロジックを共有できるようにするため。
 */
import type { MasterHistoryType } from '../api/types';
import {
  PAGE_PATH_BROADCAST_POINTS,
  PAGE_PATH_CHIMES,
  PAGE_PATH_DEVICES,
  PAGE_PATH_FREE_TEXT_TEMPLATES,
  PAGE_PATH_MASTER_HISTORY,
  PAGE_PATH_PERMISSION_GROUPS,
  PAGE_PATH_PHRASE_TEMPLATES,
  PAGE_PATH_SSML_DICTIONARY,
} from '../constants/pagePaths';
import { MASTER_HISTORY_FEATURE_BY_TYPE } from '../permissions/masterHistoryFeatures';

/** 履歴画面の URL クエリ名 (変更通知メールのリンクと同じ) */
export const MASTER_HISTORY_PARAM = {
  masterType: 'master_type',
  entityKey: 'entity_key',
  rev: 'rev',
} as const;

/** 履歴の版 (ULID。Crockford Base32 の 26 文字・大文字)。バックエンドの検証と同じ形式 */
const VERSION_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

const KNOWN_MASTER_TYPES: ReadonlySet<string> = new Set(
  Object.keys(MASTER_HISTORY_FEATURE_BY_TYPE),
);

export type MasterHistoryView =
  | {
      kind: 'list';
      /** ① の絞り込み。未指定・未知の値は undefined */
      masterType: MasterHistoryType | undefined;
    }
  | {
      kind: 'entity';
      entityKey: string;
      masterType: MasterHistoryType | undefined;
      /** `rev` が指定されたが版の形式として不正で、③ を開けなかった (案内メッセージを出す) */
      invalidRev: boolean;
    }
  | {
      kind: 'detail';
      entityKey: string;
      masterType: MasterHistoryType | undefined;
      rev: string;
    };

function readMasterType(params: URLSearchParams): MasterHistoryType | undefined {
  const value = params.get(MASTER_HISTORY_PARAM.masterType);
  return value !== null && KNOWN_MASTER_TYPES.has(value) ? (value as MasterHistoryType) : undefined;
}

/**
 * `entity_key` (`<master_type>#<主キー...>`) の先頭からマスタ種別を取り出す。
 * 既知の種別でなければ undefined (権限の判定には使えない)。
 * バックエンドの `entity_master_type` と同じく、`#` の区切りが無いものは種別とみなさない。
 */
export function masterTypeOfEntityKey(entityKey: string): MasterHistoryType | undefined {
  const separator = entityKey.indexOf('#');
  if (separator < 0) return undefined;
  const head = entityKey.slice(0, separator);
  return KNOWN_MASTER_TYPES.has(head) ? (head as MasterHistoryType) : undefined;
}

/** 履歴の元になった現場WG画面 (マスタ種別ごと) の URL パス */
const SOURCE_PATH_BY_TYPE: Readonly<Record<MasterHistoryType, string>> = {
  broadcast_spot: PAGE_PATH_BROADCAST_POINTS,
  chime: PAGE_PATH_CHIMES,
  phrase_template: PAGE_PATH_PHRASE_TEMPLATES,
  ssml_dictionary: PAGE_PATH_SSML_DICTIONARY,
  free_text_template: PAGE_PATH_FREE_TEXT_TEMPLATES,
  permission_group: PAGE_PATH_PERMISSION_GROUPS,
  device: PAGE_PATH_DEVICES,
};

/** 履歴から「遷移元の現場WG画面」へ戻る URL パス */
export function buildSourceScreenPath(masterType: MasterHistoryType): string {
  return SOURCE_PATH_BY_TYPE[masterType];
}

/**
 * ヘッダーのタブから履歴画面へ入ったことを表すルーターの state。
 *
 * タブから入った履歴には「遷移元」が無い (各データの「履歴を見る」から入ったときだけ、
 * 閉じて遷移元に戻る / 遷移元へ戻る導線が意味を持つ)。`window.opener` では区別できない。
 * 履歴ウィンドウの中でタブを押したときも opener が残るため、遷移の経路を state で渡す。
 */
export const MASTER_HISTORY_NAV_STATE = { openedFromNav: true } as const;

/** ルーターの state が「タブから入った」ことを示すか */
export function isOpenedFromNav(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { openedFromNav?: unknown }).openedFromNav === true
  );
}

/** ① の絞り込み条件を載せた URL (`master_type` だけを持つ。空なら `/master-history`) */
export function buildListPath(masterType?: MasterHistoryType): string {
  return masterType
    ? `${PAGE_PATH_MASTER_HISTORY}?${MASTER_HISTORY_PARAM.masterType}=${encodeURIComponent(masterType)}`
    : PAGE_PATH_MASTER_HISTORY;
}

/**
 * ② 対象データの履歴一覧の URL。
 *
 * `masterType` を渡すと変更通知メールのリンクと同じ形 (`master_type` + `entity_key`) になる。
 * 画面内の遷移 (①→②) は `entity_key` だけで足りるので省略できる。
 */
export function buildEntityPath(entityKey: string, masterType?: MasterHistoryType): string {
  const entity = `${MASTER_HISTORY_PARAM.entityKey}=${encodeURIComponent(entityKey)}`;
  return masterType
    ? `${PAGE_PATH_MASTER_HISTORY}?${MASTER_HISTORY_PARAM.masterType}=${encodeURIComponent(masterType)}&${entity}`
    : `${PAGE_PATH_MASTER_HISTORY}?${entity}`;
}

/** ③ 履歴詳細の URL (メールのリンクと同じく `entity_key` と `rev` を持つ) */
export function buildDetailPath(entityKey: string, version: string): string {
  return `${buildEntityPath(entityKey)}&${MASTER_HISTORY_PARAM.rev}=${encodeURIComponent(version)}`;
}

/**
 * URL のクエリから表示する階層を決める。
 *
 * - 空文字の値は未指定と同じ扱い (`?entity_key=` は ① )。
 * - `entity_key` が無ければ `rev` は無視する (版は対象データの中でしか意味を持たないため)。
 * - 同じキーが複数あれば最初の値を使う (`URLSearchParams.get` の仕様)。
 * - `rev` が版の形式でなければ ② にして `invalidRev` を立てる。
 */
export function resolveMasterHistoryView(params: URLSearchParams): MasterHistoryView {
  const masterType = readMasterType(params);
  const entityKey = params.get(MASTER_HISTORY_PARAM.entityKey);
  if (!entityKey) {
    return { kind: 'list', masterType };
  }
  const rev = params.get(MASTER_HISTORY_PARAM.rev);
  if (!rev) {
    return { kind: 'entity', entityKey, masterType, invalidRev: false };
  }
  if (!VERSION_PATTERN.test(rev)) {
    return { kind: 'entity', entityKey, masterType, invalidRev: true };
  }
  return { kind: 'detail', entityKey, masterType, rev };
}
